import he from 'he';
import fp from 'lodash/fp';
import React, { Component } from 'react';
import { Button, Col, Grid, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import { compose, withProps } from 'recompose';
import { bindActionCreators } from 'redux';
import { routes } from '../../../constants';
import { businessActions } from '../../../state/modules/business';
import { dataGovActions } from '../../../state/modules/data-gov';
import { globalActions } from '../../../state/modules/global';
import { industryActions } from '../../../state/modules/industry';
import { userActions } from '../../../state/modules/user';
import { Icon, TypeaheadField } from '../../components';
import Loading from '../loading';
import { getIndustryFromIdentifier } from './industry';

/**
 * Provides AFS licensees searching page.
 * Onboarding Users can provide their AFS licence number here.
 */
class AfsLicence extends Component {

  constructor() {
    super();

    this.state = {
      selectedAfsLicenceNumber: null,
      userUpdated: false
    };
  }

  componentDidMount() {
    if (!this.props.user.currentUser.id) {
      this.props.getUser();
    } else {
      this.props.getUserIndustryRoles();
      this.props.getIndustries();
    }
  }

  onAfsSearchChange(value) {
    this.props.searchAfsLicensees(value);
  }

  onAfsLicenseeSelected(value) {
    this.setState({
      selectedAfsLicenceNumber: value && value.length > 0 ?
        value[0] :
        null
    });
  }

  updateAfsForUser(e) {
    e.preventDefault();

    if (this.state.selectedAfsLicenceNumber) {
      // Currently we only support one User to one Business, this may be changed in the future
      // to support multiple Businesses to one User.
      const userDefaultBusiness = fp.head(this.props.business.businesses);

      const afsLicence = {
        userId: this.props.user.currentUser.id,
        afsLicenceNumber: this.state.selectedAfsLicenceNumber.number,
        afsLicenceHolder: this.state.selectedAfsLicenceNumber.businessName,
        businessId: userDefaultBusiness && userDefaultBusiness.id
      };

      this.setState({ userUpdated: true });

      const selectedCpdCategory = [];
      const nextUrl = routes.onboarding.cpdCategorys;

      this.props.selectCpdCategorys(selectedCpdCategory);
      if (afsLicence.businessId) {
        this.props.addBusinessAfsl(afsLicence, nextUrl);
      }
      else {
        this.props.addUserAfsl(afsLicence, nextUrl);
      }
    }
  }

  /**
   * Renders the search results for type ahead.
   * @see https://github.com/ericgio/react-bootstrap-typeahead/blob/master/docs/Props.md
   */
  renderAfsLicensee(option, props, index) {
    return he.decode(`${option.businessName} - (${option.number})`);
  }

  render() {
    if (!this.props.user.currentUser.id) {
      return <Loading />;
    }

    const tooltip = (overlayRef) => (
      <Tooltip id='afsl-tooltip'>
        <Button onClick={() => overlayRef.hide()} className='pull-right full-width close-overlay' bsStyle='plain'>
          <Icon icon='close' />
        </Button>
        <p><strong>Australian Financial Services Licence (ASFL)</strong></p>
        <p>Your AFSL (Australian Financial Services Licence) is a licence given by ASIC that allows people or companies to legally carry on a financial services business, including selling, advising or dealing in financial products.</p>
      </Tooltip>
    );

    return (
      <section id='onboarding'>
        <Grid>
          <Row>
            <Col xs={12} md={8} mdOffset={2}>
              <h1 className="text-center">Which Australian financial services licence (AFSL) do you operate under?</h1>
              <p>
                <OverlayTrigger ref={(ref) => this.overlay = ref} placement='bottom' overlay={tooltip(this.overlay)}>
                  <Button bsStyle='plain'><Icon icon='help' className='hide-on-ie' /> <span>What's this?</span></Button>
                </OverlayTrigger>
              </p>
            </Col>
            <form onSubmit={(e) => this.updateAfsForUser(e)}>
              <Row>
                <Col xs={12} md={6} mdOffset={3}>
                  <div style={{ color: 'red', textAlign: 'center' }}>
                    {this.state.userUpdated && this.props.user.error ? this.props.user.error : ''}
                  </div>
                  <fieldset className='afsl-search'>
                    <TypeaheadField
                      label='Search for licence holder or AFSL number'
                      icon='search-large'
                      searchText={this.state.searchText}
                      isLoading={this.props.datagov.isFetching}
                      emptyLabel={this.props.datagov.isFetching ? 'Searching...' : 'No matches'}
                      options={this.props.datagov ? this.props.datagov.afsLicensees : []}
                      labelKey={this.renderAfsLicensee}
                      renderMenuItemChildren={this.renderAfsLicensee}
                      onSearch={(v) => this.onAfsSearchChange(v)}
                      onChange={(v) => this.onAfsLicenseeSelected(v)}
                      onInputChange={(v) => this.setState({ searchText: v })}
                    />
                  </fieldset>
                </Col>
              </Row>
              <Row className='no-gutter submit-row afsl-search'>
                <Col xs={12} md={6} mdOffset={3}>
                  <Button type='submit' className='large' disabled={this.state.selectedAfsLicenceNumber ? false : true}>Continue</Button>
                  <p>
                    <LinkContainer className='full' exact to={routes.onboarding.cpdCategorys}>
                      <Button bsStyle='plain'>I'll do this later - skip this step</Button>
                    </LinkContainer>
                  </p>
                </Col>
              </Row>
            </form>
          </Row>
        </Grid>
      </section>
    )
  }
}

const mapStateToProps = (state) => ({
  datagov: state.datagov,
  user: state.user,
  business: state.business,
  industry: state.industry,
  industriesWithCpdCategorys: state.industry.industriesWithCpdCategorys
});

const mapDispatchToProps = (dispatch) => ({
  searchAfsLicensees: bindActionCreators(dataGovActions.dataGov.licensees.get, dispatch),
  addUserAfsl: bindActionCreators(userActions.user.addAfsl, dispatch),
  addBusinessAfsl: bindActionCreators(businessActions.business.addAfsLicence, dispatch),
  selectCpdCategorys: bindActionCreators(globalActions.onboarding.selectCpdCategorys, dispatch),
  getUser: bindActionCreators(userActions.user.getCurrent, dispatch),
  getUserIndustryRoles: bindActionCreators(userActions.user.getIndustryRoles, dispatch),
  getIndustries: bindActionCreators(industryActions.industries.get, dispatch)
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProps(({
    match: {
      path,
      params: {
        industryIdentifier
      }
    },
    industriesWithCpdCategorys
  }) => ({
    currentUserIndustry: getIndustryFromIdentifier(industryIdentifier, industriesWithCpdCategorys)
  }))
)(AfsLicence);



// WEBPACK FOOTER //
// ./src/containers/pages/onboarding/afs-licence.js